// pfs-scope-toggle.ts — Segmented single-select Toggle (PFS-593).
//
// Ersetzt den je Cut-App-Bundle KOPIERTEN Helfer buildScopeToggle (identity,
// practice — byte-identisch). Ein Wert aus einer kleinen Optionsmenge, als
// Button-Segmente (selektiert = primary, sonst secondary). Typisch der
// Aktiv/Gelöscht/Alle-Scope einer Lifecycle-Liste, links neben der Suche.
//
// Generisch (@pfs/components-Boundary): Labels kommen vom Consumer via
// el.options — KEINE Domain-Texte hier. Die Optik repliziert .btn.sm aus
// @pfs/design token-basiert im eigenen Shadow → immun gegen WP/Tailwind-Bleed,
// auch am document.body / als data-list-Slot nutzbar.
//
// API:
//   Property el.options = [value,label] | {value,label} | "str" — Array.
//   Property el.value    (get/set) — aktuelle Auswahl.
//   data-value           INITIAL-Auswahl (vor dem ersten options-Set lesbar).
//   Property el.onChange(value) — optionaler Callback bei Auswahländerung
//     (imperative Bequemlichkeit für Builder wie pfs-list; PFS-664).
//
// Events (bubbles + composed):
//   change  — native Form-Event bei Nutzer-Auswahl (nicht bei set); Wert via
//     el.value. Uniform mit text/select/radio (PFS-663), NICHT pfs:change.
//
// Layout: das Rechtsbündig-Schieben (margin-left:auto in der Toolbar) bleibt
//   Consumer-Sache — die WC stylt nur SICH selbst (:host = inline-flex).
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import { invokeCallback } from "../../utils/invoke-callback.js";
import {
  installFieldValue,
  bindSelectionValue,
  readFieldValue,
} from "../../composition/field-value.js";

const STYLES = `
:host { display: inline-flex; }
.seg-group { display: inline-flex; gap: 0.3rem; }
.seg {
  font: inherit;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.3;
  padding: 0.2rem 0.6rem;
  border-radius: var(--pfs-radius-sm, 0.375rem);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.seg:focus-visible { outline: none; box-shadow: var(--pfs-shadow-focus-ring-strong, 0 0 0 3px rgba(37, 99, 235, 0.45)); }
.seg.on {
  background: var(--pfs-color-primary);
  border-color: var(--pfs-color-primary);
  color: var(--pfs-color-on-primary);
}
.seg.off {
  background: var(--pfs-color-surface);
  border-color: var(--pfs-color-outline-strong);
  color: var(--pfs-color-on-surface-muted);
}
.seg.off:hover { background: var(--pfs-color-surface-hover); }
`;

interface Opt {
  value: string;
  label: string;
}

/** Option-Eingabeform: String, [value,label]-Tupel ODER {value,label}. */
export type ScopeOption = string | [string, string] | { value: string; label?: string };

/** Typisierte Consumer-Sicht auf <pfs-scope-toggle>. */
export interface PfsScopeToggleElement extends HTMLElement {
  options: ScopeOption[];
  value: string;
  onChange?: (value: string) => void;
}

const normalize = (o: unknown): Opt => {
  if (typeof o === "string") return { value: o, label: o };
  if (Array.isArray(o)) {
    const value = o[0] == null ? "" : String(o[0]);
    return { value, label: o[1] == null ? value : String(o[1]) };
  }
  const r = o as { value?: unknown; label?: unknown };
  const value = r.value == null ? "" : String(r.value);
  return { value, label: r.label == null ? value : String(r.label) };
};

defineComponent({
  tagName: "pfs-scope-toggle",
  mixins: [withShadow()],
  // .value ab Konstruktion pre-connect-sicher (PFS-650, uniform mit text/select).
  created: installFieldValue,
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const group = document.createElement("div");
    group.className = "seg-group";
    group.setAttribute("role", "group");
    root.appendChild(group);

    let opts: Opt[] = [];
    const self = host as PfsScopeToggleElement;
    const preOptions = takeOwnProperty<ScopeOption[]>(host, "options");

    // Puffer (field-value) ist die Wahrheit → renderSegs liest ihn via
    // readFieldValue; die Reihenfolge options/value ist damit egal.
    const renderSegs = (): void => {
      const cur = readFieldValue(host);
      group.textContent = "";
      for (const o of opts) {
        const on = o.value === cur;
        const b = document.createElement("button");
        b.type = "button";
        b.className = "seg " + (on ? "on" : "off");
        b.textContent = o.label;
        b.setAttribute("aria-pressed", String(on));
        b.addEventListener("click", () => {
          if (o.value === readFieldValue(host)) return;
          commit(o.value);
          // Native change (Wert via el.value) — uniform mit text/select/radio.
          host.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
          // sync + async (fire-and-forget) tolerant
          invokeCallback(self.onChange, o.value);
        });
        group.appendChild(b);
      }
    };

    // Auswahl-Naht: der `.value`-Setter (aus .created) spiegelt via renderSegs;
    // commit() schreibt bei User-Klick den Puffer + spiegelt.
    const commit = bindSelectionValue(host, renderSegs);

    Object.defineProperty(host, "options", {
      configurable: true,
      get: () => opts,
      set: (v: unknown) => {
        opts = Array.isArray(v) ? v.map(normalize) : [];
        renderSegs();
      },
    });

    if (preOptions !== undefined) self.options = preOptions;

    renderSegs();
  },
});
